import { CAST_AI } from '@cast/constants';
import {
  Button,
  DialogCard,
  DialogFooterV2,
  DialogHeaderV2,
  Inset,
  SwipeableViews,
  makeDialog,
  useCloseDialog,
  Typography,
  Stack,
} from '@cast/design-system';
import { useStartTrialMutation } from '@cast/shared-trial-data-access';

import { FeedbackMessageSlide } from '../_components/FeedbackMessageSlide';
import { TrialFeaturesComparison } from '../_components/TrialFeaturesComparison/TrialFeaturesComparison';
import { TRIAL_CLUSTER_LIMIT } from '../constants';

type ExhaustedTrialSlideProps = {
  onConfirm: () => void;
  isLoading: boolean;
};

const ExhaustedTrialSlide = ({
  onConfirm,
  isLoading,
}: ExhaustedTrialSlideProps) => {
  return (
    <Stack data-testid="exhausted-trial-slide">
      <DialogHeaderV2 title={`Try ${CAST_AI} for free today`} />

      <Inset mb={24}>
        <Stack gap={24}>
          <Typography variant="P12R" color="textSecondary">
            Looks like you want to connect more clusters! Unfortunately,
            you&apos;ve reached the limit of {TRIAL_CLUSTER_LIMIT} connected
            clusters in the free version. Start your 30-day free trial now to
            get unlimited clusters and our full automation suite.
          </Typography>

          <TrialFeaturesComparison />
        </Stack>
      </Inset>

      <DialogFooterV2
        endContent={
          <Button
            testId="start-trial-button"
            onClick={onConfirm}
            loading={isLoading}
          >
            Start trial
          </Button>
        }
        divider
      />
    </Stack>
  );
};

const ExhaustedTrialDialog = () => {
  const {
    isSuccess,
    isIdle,
    isPending,
    mutate: startTrial,
  } = useStartTrialMutation();
  const closeDialog = useCloseDialog<boolean>();

  return (
    <DialogCard
      stopClickPropagation
      testId="trial-exhausted-dialog"
      result={isSuccess}
    >
      <SwipeableViews
        slides={{
          main: (
            <ExhaustedTrialSlide
              onConfirm={() => startTrial()}
              isLoading={isPending}
            />
          ),
          feedback: (
            <FeedbackMessageSlide
              success={isSuccess}
              onContinue={() => closeDialog(isSuccess)}
            />
          ),
        }}
        activeSlide={isIdle || isPending ? 'main' : 'feedback'}
      />
    </DialogCard>
  );
};

export const useExhaustedTrialDialog = makeDialog<undefined, boolean>(
  ExhaustedTrialDialog
);
